import React from 'react';
import { bool, number, string } from 'prop-types';

// Utils
import { Box, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import AdjustIcon from '@mui/icons-material/Adjust';
import { formatTime } from 'feature/loading-management/utils/timeHelpers';

const MinuteChip = ({ active, data, time }) => {
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up('md'), { noSsr: true });

  // determine text and bg color for chip based on it's time data
  const getStyle = timedata => {
    if (timedata <= 30)
      return {
        bgColor: active ? theme.palette.primary.light : theme.palette.secondary.light,
        textColor: theme.palette.text.primary,
      };
    if (timedata > 30 && timedata <= 60)
      return {
        bgColor: active ? theme.palette.secondary.main : theme.palette.secondary.medium,
        textColor: active ? theme.palette.surface.white : theme.palette.text.primary,
      };
    if (timedata > 60 && timedata <= 90) return { bgColor: theme.palette.secondary.medium };
    if (timedata > 90) return { bgColor: theme.palette.secondary.dark, textColor: 'white' };
    return { bgColor: theme.palette.graphic.light, textColor: theme.palette.text.primary };
  };

  const chipStyle = getStyle(data);

  const renderData = () => {
    if (data && data !== 0) {
      return mdUp ? data : `${data} min`;
    }
    return 'N/A';
  };

  const renderTitle = () => {
    const now = formatTime(0, false);
    // formatting the time string from XX:XX in 24 hour time format
    // to 12 hour format with AM/PM added to it
    const H = +time.substr(0, 2);
    const h = H % 12 || 12;
    const ampm = H < 12 || H === 24 ? 'AM' : 'PM';
    const formatted = `${h}${time.substr(2)} ${ampm}`;
    const isNow = now === formatted;
    const returned = isNow ? (
      <Box
        sx={{
          ...theme.typography.caption,
          alignItems: 'center',
          color: 'surface.white',
          display: 'flex',
          fontWeight: 400,
          justifySelf: { md: 'end', xs: 'start' },
          position: 'relative',
        }}
      >
        <AdjustIcon
          sx={{
            color: 'error.light',
            height: 10,
            mr: '2px',
            // aligns the icon "center" to text
            mt: '-2px',
            width: 10,
          }}
        />
        LIVE: {formatted}, {data} mins
      </Box>
    ) : (
      `${formatted}: ${data} mins`
    );
    return returned;
  };

  return (
    <Box
      sx={{
        '&:last-child': {
          mr: { md: 0, xs: '24px' },
        },
        alignItems: 'center',
        backgroundColor: chipStyle?.bgColor,
        borderRadius: '2px',
        color: chipStyle?.textColor,
        display: 'flex',
        height: { md: '20px', xs: '30px' },
        justifyContent: 'center',
        justifySelf: { md: 'initial', xs: 'start' },
        padding: { md: '2px 12px 0', xs: 0 },
        width: { md: '23px', xs: '66px' },
      }}
    >
      {mdUp ? (
        <Tooltip
          enterTouchDelay={10}
          placement="top"
          slotProps={{
            tooltip: {
              sx: {
                ...theme.typography.caption,
                color: 'surface.white',
                fontWeight: 400,
              },
            },
          }}
          title={renderTitle()}
        >
          <Typography color="inherit" sx={{ fontWeight: 400 }} variant="caption">
            {renderData()}
          </Typography>
        </Tooltip>
      ) : (
        renderData()
      )}
    </Box>
  );
};

MinuteChip.propTypes = {
  active: bool.isRequired,
  data: number,
  time: string,
};

MinuteChip.defaultProps = {
  data: null,
  time: '',
};

export default MinuteChip;
